<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref, watch} from "vue";
import {useTrainingPlanStore} from "@/stores/Training/TrainingPlan.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useSharedSelectData from "@/use/SharedSelectData";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        lockAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },

    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay, inputColor, backgroundColor} = useSharedData();
        const {$route, $refs} = useTools();
        const {trainingPeriods, quarterlies, halfYearlies, months} = useSharedSelectData();
        const {getBack} = useSharedMethod();

        const trainingPlanStore = useTrainingPlanStore();

        const readonly = computed(() => props.viewAction || props.lockAction || props.deleteAction);

        const trainingPlan = ref({
            taxPrice: 0,
            extraPrice: 0,
            price: 0,
            acceptedPrice: 0,
            refusedPrice: 0,
            totalRemain: 0,
            totalPrice: 0,
            open: true,
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/trainingPlans/${id}`).then((response) => {
                trainingPlan.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/trainingPlans', trainingPlan.value).then((response) => {
                    trainingPlanStore.addTrainingPlan(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/trainingPlans', trainingPlan.value).then((response) => {
                    trainingPlanStore.updateTrainingPlan(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function lock() {
            trainingPlan.value.open = false;
            update();
        }

        function remove() {
            loading.value = true;
            axios.delete(`/trainingPlans/${trainingPlan.value.id}`).then(() => {
                trainingPlanStore.deleteTrainingPlan(trainingPlan.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        watch(
            () => [trainingPlan.value.taxPrice, trainingPlan.value.extraPrice],
            ([newTax, newExtra]) => {
                // Convert to number to prevent string concatenation
                const tax = parseFloat(newTax) || 0;
                const extra = parseFloat(newExtra) || 0;

                trainingPlan.value.price = tax + extra;
            }
        );

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            trainingPeriods,
            quarterlies,
            halfYearlies,
            months,
            trainingPlan,
            inputColor,
            backgroundColor,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            save,
            update,
            lock,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        max-width="800"
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :lock-action="lockAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :lock-action="lockAction"
                :delete-action="deleteAction"
                @lock="lock"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>

            <v-card-text>
                <v-form ref="form" lazy-validation>
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row>
                                <v-col cols="6">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-text-field
                                        v-model="trainingPlan.code"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.name')" required/>
                                    <w-text-field
                                        v-model="trainingPlan.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('common.year')" required/>
                                    <w-text-field
                                        v-model="trainingPlan.year"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('common.from')" required/>
                                    <w-date-picker
                                        v-model="trainingPlan.start"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('common.to')" required/>
                                    <w-date-picker
                                        v-model="trainingPlan.end"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('training.plan_tax_price')" required/>
                                    <w-text-field
                                        v-model="trainingPlan.taxPrice"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('training.plan_extra_price')" required/>
                                    <w-text-field
                                        v-model="trainingPlan.extraPrice"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('training.price')" required/>
                                    <w-text-field
                                        v-model="trainingPlan.price"
                                        type="number"
                                        readonly
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.comment')"/>
                                    <w-simple-text-area
                                        v-model="trainingPlan.observation"
                                        type="number"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-lock
                    :show="lockAction"
                    :loading="loading"
                    @press="overlay = true"
                />
                <w-btn-delete
                    :show="deleteAction"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>